import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { X, Mail, User, Loader2, Briefcase, Phone } from 'lucide-react';

const EmailGateModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [designation, setDesignation] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    // Allow empty or valid phone number (10+ digits)
    if (!phone.trim()) return true;
    return /^[\d\s\-\+\(\)]{10,}$/.test(phone.replace(/\s/g, ''));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Please enter your name';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Please enter your email';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!designation.trim()) {
      newErrors.designation = 'Please enter your designation';
    }

    if (!contactNumber.trim()) {
      newErrors.contactNumber = 'Please enter your contact number';
    } else if (!validatePhone(contactNumber)) {
      newErrors.contactNumber = 'Please enter a valid contact number';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    onSubmit({ 
      name: name.trim(), 
      email: email.trim(),
      designation: designation.trim(),
      contactNumber: contactNumber.trim()
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid="email-gate-modal"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#005858] to-[#0E302A] px-6 py-6 text-center">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="h-5 w-5" />
              </button>
              
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mail className="h-7 w-7 text-white" />
              </div>
              
              <h2 className="text-xl font-serif font-bold text-white mb-1">
                Your Results Are Ready
              </h2>
              <p className="text-white/80 text-sm">
                Enter your details to receive your FAB Diagnostic Report.
              </p>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Name field */}
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-[#0A0A0A] font-medium text-sm">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#787A7A]" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`pl-10 h-11 ${errors.name ? 'border-red-500' : 'border-[#E2E8E8]'}`}
                    data-testid="investor-name-input"
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-xs">{errors.name}</p>
                )}
              </div>

              {/* Designation field */}
              <div className="space-y-1.5">
                <Label htmlFor="designation" className="text-[#0A0A0A] font-medium text-sm">
                  Designation <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#787A7A]" />
                  <Input
                    id="designation"
                    type="text"
                    placeholder="Enter your designation"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    className={`pl-10 h-11 ${errors.designation ? 'border-red-500' : 'border-[#E2E8E8]'}`}
                    data-testid="investor-designation-input"
                  />
                </div>
                {errors.designation && (
                  <p className="text-red-500 text-xs">{errors.designation}</p>
                )}
              </div>
              
              {/* Email field */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[#0A0A0A] font-medium text-sm">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#787A7A]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`pl-10 h-11 ${errors.email ? 'border-red-500' : 'border-[#E2E8E8]'}`}
                    data-testid="investor-email-input"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-xs">{errors.email}</p>
                )}
              </div>

              {/* Contact Number field */}
              <div className="space-y-1.5">
                <Label htmlFor="contactNumber" className="text-[#0A0A0A] font-medium text-sm">
                  Contact Number <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#787A7A]" />
                  <Input
                    id="contactNumber"
                    type="tel"
                    placeholder="Enter your contact number"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    className={`pl-10 h-11 ${errors.contactNumber ? 'border-red-500' : 'border-[#E2E8E8]'}`}
                    data-testid="investor-contact-input"
                  />
                </div>
                {errors.contactNumber && (
                  <p className="text-red-500 text-xs">{errors.contactNumber}</p>
                )}
              </div>
              
              <p className="text-xs text-[#787A7A] text-center pt-1">
                By submitting, you agree to receive your diagnostic report and related communications from TBNG Capital Advisors.
              </p>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-[#005858] hover:bg-[#0E302A] text-white font-medium rounded-lg transition-all"
                data-testid="submit-diagnostic-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'View My Results'
                )}
              </Button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailGateModal;
